import React from 'react';
import { Breadcrumb, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setCurrentFolder, setSelectedDocument, setViewType, setHighlightedDocumentId } from 'store/slices/dataDocumentsSlice';
import Assets from 'assets/icons';
import { documnentIcons, getCorrectIcon } from 'helpers/documentIcons';
import IDocument from 'interfaces/IDocument';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import classes from './SearchResultItem.module.scss';
import api from 'api';
import RowLoader from 'components/Sceletons/RowLoader';

const getHighlightedName = (name: string, query?: string): React.ReactNode => {
  const trimmedQuery = query?.trim();
  if (!trimmedQuery) return name;

  const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');

  return name.split(regex).map((part, index) =>
    part.toLowerCase() === trimmedQuery.toLowerCase() ? (
      <em key={index} className="search-highlight">
        {part}
      </em>
    ) : (
      part
    )
  );
};

interface SearchResultItemProps {
  document: IDocument;
  context?: string;
  searchQuery?: string;
  onClose: () => void;
}

const SearchResultItem = ({ document, context, searchQuery, onClose }: SearchResultItemProps) => {
  const [docFolderPath, setDocFolderPath] = React.useState<{title: string}[]>([]);
  const [loadingPath, setLoadingPath] = React.useState(true);

  const [openFileToltip, setOpenFileToltip] = React.useState(false);
  const [openPathToltip, setOpenPathToltip] = React.useState(false);

  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { userData } = useAppSelector((state) => state.userData);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const iconKey = getCorrectIcon(document.extension?.toLowerCase() || '');
  const icon = documnentIcons[iconKey];

  const date = document.modified_at || document.upload_at;
  const formattedDate = date ? getDateWithTimeZone(date, userData?.date_format || 'international') : '';

  const tooltipContent = (
    <div className={classes.tooltipContent}>
      <div className={classes.tooltipName}>{document.name}</div>
      {formattedDate && (
        <div className={classes.tooltipDate}>
          {t('Search.tooltip.updatedAt', { date: formattedDate })}
        </div>
      )}
    </div>
  );

  const handleDoubleClick = () => {
    onClose();    
    dispatch(setSelectedDocument(document));
    dispatch(setViewType('Document'));
    navigate(`${window.location.pathname}?modalSection=view&document_id=${document.id}`);
  };

  const handleNavigate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();

    const folderId = document.folder_id;
  
    if (folderId) {
      try {
        const folder = await api.getFolderById(folderId, dataRoom?.id!);
        const folderPath = folder.data.folder_path ? folder.data.folder_path.reverse() : [];
        localStorage.setItem('previousFolderPath', JSON.stringify(folderPath));
        dispatch(setCurrentFolder(folderPath));
      } catch {
        dispatch(setCurrentFolder([]));
      }
  
      dispatch(setSelectedDocument(null));
      dispatch(setHighlightedDocumentId(document.id));
      navigate(`/room/${dataRoom?.id}/documents/${folderId}`);
    } else {
      dispatch(setCurrentFolder([]));
      dispatch(setSelectedDocument(null));
      dispatch(setHighlightedDocumentId(document.id));
      localStorage.setItem('previousFolderPath', JSON.stringify([]));
      navigate(`/room/${dataRoom?.id}/documents`);
    }
  };

  const navigationTitle = (
    <div>
      <div>{t('Search.navigation')}</div>
      {loadingPath
        ? <RowLoader width={200} height={18} forceDark={true} padding={'2px 0 0 0'}/>
        : <Breadcrumb items={docFolderPath} separator=' > ' className={classes.breadcrumbs}/>
      }
    </div>
  );

  const highlightedContext = context
    ? context.replace(/<em>/g, '<em class="search-highlight">') 
    : context;

  const onHover = async () => {
    if (document?.folder_id) {
      try {
        const folder = await api.getFolderById(String(document?.folder_id), String(dataRoom?.id))
        const folderPath = folder.data.folder_path ? folder.data.folder_path?.reverse() : [];
        const onlyFolderNames = folderPath.map(folder => ({title: folder.name}))
        setDocFolderPath([{title: t('Documents.management.rootFolder')}, ...onlyFolderNames])
      } catch (e) {

      }
    } else {
      setDocFolderPath([{title: t('Documents.management.rootFolder')}])
    }
    setLoadingPath(false)
  };

  return (
    <Tooltip
      title={tooltipContent}
      onOpenChange={(open) => setOpenFileToltip(open)}
      open={openFileToltip && !openPathToltip}
      placement='leftTop'
      mouseEnterDelay={0.6}
      classNames={{ root: cn(classes.itemTooltip, getTheme()) }}
    >
      <div className={classes.item} onDoubleClick={handleDoubleClick}>
        <div className={classes.itemIcon}>{icon}</div>

        <div className={classes.itemContent}>
          <div className={classes.itemName}>
            {getHighlightedName(document.name, searchQuery)}
          </div>
          {context && (
            <div
              className={classes.itemContext}
              dangerouslySetInnerHTML={{ __html: highlightedContext || '' }}
            />
          )}
        </div>

        <div className={classes.itemActions}>
          {document.is_confidential && (
            <Tooltip title={t('Documents.table.confidential')} classNames={{ root: getTheme() }} mouseEnterDelay={0.4}>
              <span className={classes.shieldIcon}>
                <Assets.ShieldIcon />
              </span>
            </Tooltip>
          )}
          <Tooltip
            onOpenChange={(open) => setOpenPathToltip(open)}
            open={openPathToltip}
            title={navigationTitle}
            classNames={{ root: getTheme() }}
            mouseEnterDelay={0.4}
            styles={{root: {maxWidth: '550px'}}}
          >
            <span onMouseEnter={onHover} className={classes.navButton} onClick={handleNavigate}>
              <Assets.RightIcon />
            </span>
          </Tooltip>
        </div>
      </div>
    </Tooltip>
  );
};

export default SearchResultItem;
